/**
 * MietgoCrossLinkBadge — Issue #501 (Mietgo Quick-Button)
 * --------------------------------------------------------
 * Shown in the Gutachten-Akte header area when `mietwagen_in_anspruch=true`.
 *
 * Two rendering modes:
 *   1. LINKED   — `mietwagen_akte_id` is set: full cross-link banner to the
 *                 Mietwagen-Akte (delegated to LinkedAkteBanner).
 *   2. PENDING  — flag is set but no akte linked yet: compact amber badge
 *                 "Mietwagen in Anspruch genommen".
 *
 * Renders nothing when `mietwagen_in_anspruch` is false/null.
 */

import { Key } from 'lucide-react';
import LinkedAkteBanner from '@/components/akte/LinkedAkteBanner';

interface MietgoCrossLinkBadgeProps {
  mietwagen_in_anspruch: boolean | null | undefined;
  mietwagen_akte_id: string | null | undefined;
}

export default function MietgoCrossLinkBadge({
  mietwagen_in_anspruch,
  mietwagen_akte_id,
}: MietgoCrossLinkBadgeProps) {
  if (!mietwagen_in_anspruch) return null;

  if (mietwagen_akte_id) {
    return (
      <LinkedAkteBanner
        linkedAkteId={mietwagen_akte_id}
        currentBusinessUnit="gutachten"
      />
    );
  }

  // Pending: flag set but no linked mietwagen akte yet
  return (
    <div className="flex items-center gap-3 px-3 sm:px-4 py-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-[13px]">
      <span className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
        <Key className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Mietwagen</span>
          <span className="text-[13px] font-bold">in Anspruch genommen</span>
        </span>
        <span className="text-[12px] opacity-70 block">
          MietGo / LCS GmbH · Akte wird automatisch verknüpft sobald vorhanden
        </span>
      </span>
    </div>
  );
}
